import React, { useState, useEffect } from 'react';
import { Race, ScoredRace } from '../types/races';
import { processSystemRaces, countUniqueVenues } from '../utils/systemRaceProcessing';
import SystemRaceCard from '../components/system/SystemRaceCard';

export default function PublicRacesPage() {
  const [races, setRaces] = useState<ScoredRace[]>([]);
  const [rawRaces, setRawRaces] = useState<Race[]>([]);
  const [loadingRaces, setLoadingRaces] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRaces, setExpandedRaces] = useState<{ [key: string]: boolean }>({});
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded for public page
  const [stakingStrategy, setStakingStrategy] = useState<'recovery' | 'percentage'>('recovery');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [raceFilter, setRaceFilter] = useState<'all' | 'qualifying'>('all');

  const fetchTodaysRaces = async () => {
    try {
      setLoadingRaces(true);
      setError(null);
      
      // Try different backend URLs in order of preference
      const backendUrls = [
        process.env.REACT_APP_API_URL,
        'https://horse-racing-oracle-v2.fly.dev',
        'https://bot.horseracingoracle.com'
      ].filter(Boolean);
      
      let lastError = null;
      
      for (const backendUrl of backendUrls) {
        try {
          console.log(`Trying cached races from: ${backendUrl}`);
          // Use the dashboard endpoint that returns Redis cache data (works with or without authentication)
          const response = await fetch(`${backendUrl}/api/api-keys/todays-races`);
          
          if (!response.ok) {
            const responseText = await response.text();
            console.error(`API Error Response from ${backendUrl}:`, responseText);
            throw new Error(`HTTP error! status: ${response.status} - ${responseText.substring(0, 200)}`);
          }
          
          // Check if response is JSON
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const responseText = await response.text();
            console.error(`Non-JSON Response from ${backendUrl}:`, responseText);
            throw new Error('Server returned non-JSON response. Please check if the backend is running.');
          }
          
          const data = await response.json();
          setRawRaces(data);
          
          // Process with current strategy
          const minFavouriteOdds = stakingStrategy === 'percentage' ? 2.25 : 1.80;
          setRaces(processSystemRaces(data, { minFavouriteOdds }));
          setLastUpdated(new Date());
          
          console.log(`Successfully fetched ${data.length} races from cache via ${backendUrl}`);
          return; // Success, exit the function
          
        } catch (err) {
          console.error(`Failed to fetch from ${backendUrl}:`, err);
          lastError = err;
          continue; // Try next URL
        }
      }
      
      // If we get here, all URLs failed
      throw lastError || new Error('All backend URLs failed');
      
    } catch (err: any) {
      console.error('Error fetching races:', err);
      setError(err.message || 'Failed to fetch races from all available backends');
    } finally {
      setLoadingRaces(false);
    }
  };

  useEffect(() => {
    fetchTodaysRaces();
    
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchTodaysRaces, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Re-process when strategy changes
    if (rawRaces.length > 0) {
      const minFavouriteOdds = stakingStrategy === 'percentage' ? 2.25 : 1.80;
      setRaces(processSystemRaces(rawRaces, { minFavouriteOdds }));
    }
  }, [stakingStrategy, rawRaces]);

  useEffect(() => {
    // Reset expanded races when filter changes
    setExpandedRaces({});
  }, [raceFilter]);

  const toggleRaceExpanded = (marketId: string) => {
    setExpandedRaces(prev => ({
      ...prev,
      [marketId]: !prev[marketId]
    }));
  };

  const qualifyingRaces = races.filter(race => race.tag === 'Qualifying Race');
  const totalMeetings = countUniqueVenues(races);
  
  // Filter races based on current filter
  const filteredRaces = raceFilter === 'qualifying' ? qualifyingRaces : races;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Today's Races</h1>
              <p className="mt-1 text-sm text-gray-500">
                Live horse racing data from Betfair
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {lastUpdated && (
                <div className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              <button
                onClick={fetchTodaysRaces}
                disabled={loadingRaces}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loadingRaces ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
              raceFilter === 'all' ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => setRaceFilter('all')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Races</dt>
                    <dd className="text-lg font-medium text-gray-900">{races.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Meetings</dt>
                    <dd className="text-lg font-medium text-gray-900">{totalMeetings}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div 
            className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
              raceFilter === 'qualifying' ? 'ring-2 ring-yellow-500' : ''
            }`}
            onClick={() => setRaceFilter('qualifying')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Qualifying Races</dt>
                    <dd className="text-lg font-medium text-gray-900">{qualifyingRaces.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Strategy Selector */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Strategy Settings</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="strategy"
                value="recovery"
                checked={stakingStrategy === 'recovery'}
                onChange={(e) => setStakingStrategy(e.target.value as 'recovery' | 'percentage')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Daily Profit Target</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="strategy"
                value="percentage"
                checked={stakingStrategy === 'percentage'}
                onChange={(e) => setStakingStrategy(e.target.value as 'recovery' | 'percentage')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Percentage Staking</span>
            </label>
          </div>
        </div>

        {/* Races List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Race Details</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  Showing {filteredRaces.length} {raceFilter === 'qualifying' ? 'qualifying' : ''} races
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loadingRaces ? (
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-gray-50 p-4 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : filteredRaces.length > 0 ? (
              <div className="space-y-4">
                {filteredRaces.map((race) => (
                  <SystemRaceCard
                    key={race.marketId}
                    race={race}
                    isExpanded={!!expandedRaces[race.marketId]}
                    onToggle={() => toggleRaceExpanded(race.marketId)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No races found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  There are no races available at the moment.
                </p>
              </div>
            )}

            {error && (
              <div className="mt-4 p-4 rounded-md bg-red-50 text-red-800">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error loading races</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Data provided by Betfair • Updates every 2 minutes</p>
        </div>
      </div>
    </div>
  );
}
